import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Check, Plus, Sparkles } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/Breadcrumb";
import { services as allServices, getServiceBySlug } from "@/data/services";
import { Button, Eyebrow, cx } from "@/components/kit";

const WHATSAPP =
  "https://wa.me/31641262267?text=" +
  encodeURIComponent("Hoi Tim, ik wil samenwerken met UPPIT.");

const ServicePage = ({ slug }) => {
  const svc = getServiceBySlug(slug);

  const faqJsonLd = svc
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: svc.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  useSeo({
    title: svc ? svc.metaTitle : "Dienst niet gevonden",
    description: svc ? svc.metaDescription : "Deze dienst bestaat niet.",
    path: svc ? `/diensten/${svc.slug}` : "/diensten",
    ogSlug: svc ? `service/${svc.slug}` : "page/diensten",
    jsonLd: faqJsonLd,
    noindex: !svc,
  });

  if (!svc) return null;

  const Icon = svc.icon;
  const index = allServices.findIndex((s) => s.slug === svc.slug);
  const related = svc.related.map((s) => getServiceBySlug(s)).filter(Boolean);

  return (
    <article data-testid={`service-page-${svc.slug}`} className="relative pt-28 lg:pt-32 pb-24 bg-ivory">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Diensten", href: "/diensten" },
            { label: svc.title, href: `/diensten/${svc.slug}` },
          ]}
        />

        {/* Hero */}
        <div className="mt-10 lg:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-16 lg:mb-24">
          <div className="lg:col-span-7">
            <Eyebrow>{`Dienst ${String(index + 1).padStart(2, "0")} · UPPIT`}</Eyebrow>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 font-display font-black text-coal tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-balance"
            >
              {svc.h1}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-7 text-lg lg:text-xl text-coal/60 leading-relaxed max-w-2xl"
            >
              {svc.intro}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-9 flex flex-col sm:flex-row gap-3 items-start"
            >
              <Button to="/contact" variant="primary" withArrow testid="service-cta-contact" className="px-8 py-4">
                Plan strategiegesprek
              </Button>
              <Button href={WHATSAPP} target="_blank" rel="noopener noreferrer" variant="outline" testid="service-cta-whatsapp" className="px-8 py-4">
                <MessageCircle className="w-4 h-4" /> WhatsApp Tim
              </Button>
            </motion.div>
          </div>

          {/* Poster visual */}
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] rounded-[1.75rem] overflow-hidden bg-obsidian border border-obsidian-600 shadow-card-lift">
              <div className="absolute inset-0 grid-dark opacity-70" />
              <div className="glow-orb w-[320px] h-[320px] bg-electric/35 -bottom-16 -right-10" />
              <div className="relative h-full p-8 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div className="w-16 h-16 rounded-2xl glass-dark grid place-items-center text-electric-300">
                    <Icon className="w-7 h-7" />
                  </div>
                  <span className="font-display font-black text-6xl text-white/10">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-electric-300 mb-2">UPPIT dienst</div>
                  <div className="font-display font-bold text-3xl text-ivory leading-tight">{svc.title}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Main */}
          <div className="lg:col-span-8">
            <div className="space-y-12 lg:space-y-16">
              {svc.sections.map((s, i) => (
                <motion.section
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-electric mb-3">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h2 className="font-display font-bold text-2xl lg:text-4xl text-coal mb-5 leading-tight tracking-tight text-balance">
                    {s.h2}
                  </h2>
                  <p className="text-lg text-coal/65 leading-relaxed">{s.body}</p>
                  {Array.isArray(s.cards) && s.cards.length > 0 && (
                    <div className={`mt-8 grid gap-4 sm:grid-cols-2 ${s.cards.length === 3 || s.cards.length >= 5 ? "xl:grid-cols-3" : ""}`}>
                      {s.cards.map((c, j) => {
                        const CIcon = c.icon;
                        return (
                          <div
                            key={j}
                            data-testid={`pillar-card-${i}-${j}`}
                            className="group relative overflow-hidden rounded-2xl border border-ivory-400 bg-white p-6 shadow-card-light hover:border-electric/40 hover:shadow-card-lift hover:-translate-y-1 transition-all duration-300"
                          >
                            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-electric to-electric-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            <div className="flex items-center justify-between mb-5">
                              <div className="w-11 h-11 rounded-xl bg-electric/10 border border-electric/15 grid place-items-center text-electric group-hover:bg-electric group-hover:text-white transition-colors duration-300">
                                <CIcon className="w-5 h-5" />
                              </div>
                              <span className="font-mono text-[11px] tracking-[0.22em] text-coal/30">
                                {String(j + 1).padStart(2, "0")}
                              </span>
                            </div>
                            <div className="font-display font-bold text-lg text-coal mb-1.5 leading-snug">{c.title}</div>
                            <p className="text-sm text-coal/60 leading-relaxed">{c.desc}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.section>
              ))}
            </div>

            {/* Benefits */}
            <div className="mt-16 lg:mt-20 rounded-[1.5rem] border border-ivory-400 bg-white p-8 lg:p-10 shadow-card-light">
              <div className="flex items-center gap-2 mb-7">
                <Sparkles className="w-4 h-4 text-electric" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-electric">Wat je krijgt</span>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {svc.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-coal/80">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-electric/10 grid place-items-center shrink-0">
                      <Check className="w-3 h-3 text-electric" />
                    </span>
                    <span className="text-[15px] leading-snug">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* FAQ */}
            <div className="mt-16 lg:mt-20">
              <Eyebrow>FAQ · Antwoorden</Eyebrow>
              <h2 className="mt-6 font-display font-black text-2xl lg:text-4xl text-coal mb-8 leading-tight tracking-tight text-balance">
                Veelgestelde vragen over {svc.title.toLowerCase()}
              </h2>
              <div className="rounded-[1.5rem] border border-ivory-400 bg-white overflow-hidden shadow-card-light">
                {svc.faqs.map((f, i) => (
                  <details key={i} data-testid={`faq-item-${i}`} className="group border-b border-ivory-300 last:border-b-0">
                    <summary className="cursor-pointer flex items-start justify-between gap-4 list-none px-6 lg:px-8 py-6">
                      <span className="font-display font-bold text-lg text-coal leading-snug">{f.q}</span>
                      <Plus className="w-5 h-5 text-electric transition-transform duration-500 group-open:rotate-45 shrink-0 mt-1" />
                    </summary>
                    <div className="px-6 lg:px-8 pb-6 text-coal/65 leading-relaxed max-w-2xl">{f.a}</div>
                  </details>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-[1.5rem] bg-obsidian text-ivory p-7 relative overflow-hidden">
              <div className="glow-orb w-[280px] h-[280px] bg-electric/25 -top-16 -right-10" />
              <div className="relative">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-electric-300 mb-4">Klaar om te starten?</div>
                <p className="text-white/70 leading-relaxed mb-6">
                  Vraag een gratis strategiegesprek. Binnen 30 minuten weet je waar je groeikansen liggen — geen verplichtingen.
                </p>
                <Button to="/contact" variant="primary" testid="sidebar-cta-contact" className="w-full justify-center mb-3">
                  Plan gesprek
                </Button>
                <Button to="/ai-scan" variant="outlineLight" testid="sidebar-cta-aiscan" className="w-full justify-center">
                  Start AI Growth Scan
                </Button>
              </div>
            </div>

            {related.length > 0 && (
              <div className="rounded-[1.5rem] border border-ivory-400 bg-white p-7 shadow-card-light">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-coal/40 mb-5">Gerelateerde diensten</div>
                <ul className="space-y-1">
                  {related.map((r) => {
                    const RIcon = r.icon;
                    return (
                      <li key={r.slug}>
                        <Link
                          to={`/diensten/${r.slug}`}
                          data-testid={`related-${r.slug}`}
                          className="flex items-center gap-3 py-3 border-b border-ivory-300 group last:border-b-0"
                        >
                          <div className="w-9 h-9 rounded-lg bg-electric/10 border border-electric/15 grid place-items-center text-electric group-hover:bg-electric group-hover:text-white transition-colors shrink-0">
                            <RIcon className="w-4 h-4" />
                          </div>
                          <span className="font-medium text-coal group-hover:text-electric transition-colors">{r.title}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-coal/30 group-hover:text-electric ml-auto transition-colors" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </aside>
        </div>

        {/* All services strip */}
        <div className="mt-20 pt-10 border-t border-ivory-400">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-coal/40 mb-6">Alle diensten</div>
          <div className="flex flex-wrap gap-2">
            {allServices.map((s) => (
              <Link
                key={s.slug}
                to={`/diensten/${s.slug}`}
                className={cx(
                  "px-5 py-2.5 rounded-full text-sm transition-colors duration-300",
                  s.slug === svc.slug
                    ? "bg-electric text-white font-semibold"
                    : "border border-ivory-400 bg-white text-coal/70 hover:border-electric/40 hover:text-electric"
                )}
              >
                {s.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
};

export default ServicePage;
